import { getTaxonomyAPIURL } from './constants/api_urls.js';
const PUBLIC_API_KEY = process.env.PUBLIC_TAXONOMY_API_KEY;
const apiUrl = getTaxonomyAPIURL(process.env.UNIVERSAL_COMPONENTS_ENV || 'dev');
// In-memory cache for taxonomy data (fallback)
const taxonomyCache = new Map();
// SessionStorage key prefix for taxonomy cache
const TAXONOMY_CACHE_PREFIX = 'taxonomy_cache_';
/**
 * Generates a cache key for taxonomy data
 * @param locale The locale string
 * @param type The type of data ('offering' or 'seller')
 * @param uri The URI of the item
 * @returns A unique cache key string
 */
const generateCacheKey = (locale, type, uri) => {
    return `${locale}:${type}:${uri}`;
};
/**
 * Gets a value from sessionStorage cache
 * @param key The cache key
 * @returns The cached value or undefined if not found
 */
const getFromSessionStorage = (key) => {
    try {
        const item = sessionStorage.getItem(TAXONOMY_CACHE_PREFIX + key);
        if (item !== null) {
            return JSON.parse(item);
        }
    }
    catch (error) {
        console.warn('Failed to read from sessionStorage:', error);
    }
    return undefined;
};
/**
 * Sets a value in sessionStorage cache
 * @param key The cache key
 * @param value The value to cache
 */
const setToSessionStorage = (key, value) => {
    try {
        sessionStorage.setItem(TAXONOMY_CACHE_PREFIX + key, JSON.stringify(value));
    }
    catch (error) {
        console.warn('Failed to write to sessionStorage:', error);
    }
};
/**
 * Gets a value from cache (checks sessionStorage first, then in-memory)
 * @param key The cache key
 * @returns The cached value or undefined if not found
 */
const getFromCache = (key) => {
    // Try sessionStorage first
    const sessionValue = getFromSessionStorage(key);
    if (sessionValue !== undefined) {
        // Also update in-memory cache for faster subsequent access
        taxonomyCache.set(key, sessionValue);
        return sessionValue;
    }
    // Fall back to in-memory cache
    if (taxonomyCache.has(key)) {
        return taxonomyCache.get(key);
    }
    return undefined;
};
/**
 * Sets a value in cache (both sessionStorage and in-memory)
 * @param key The cache key
 * @param value The value to cache
 */
const setToCache = (key, value) => {
    // Set in sessionStorage
    setToSessionStorage(key, value);
    // Also set in in-memory cache
    taxonomyCache.set(key, value);
};
/**
 * Clears the taxonomy cache (both sessionStorage and in-memory)
 * @param keyPattern Optional pattern to match specific keys (e.g., 'en-US:offering:*')
 */
const clearTaxonomyCache = (keyPattern) => {
    if (!keyPattern) {
        // Clear in-memory cache
        taxonomyCache.clear();
        // Clear sessionStorage cache
        try {
            const keysToRemove = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith(TAXONOMY_CACHE_PREFIX)) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => sessionStorage.removeItem(key));
        }
        catch (error) {
            console.warn('Failed to clear sessionStorage cache:', error);
        }
        console.log('Taxonomy cache cleared completely');
        return;
    }
    const pattern = keyPattern.replace('*', '');
    const keysToDelete = [];
    // Clear from in-memory cache
    for (const key of taxonomyCache.keys()) {
        if (key.includes(pattern)) {
            keysToDelete.push(key);
        }
    }
    keysToDelete.forEach(key => taxonomyCache.delete(key));
    // Clear from sessionStorage
    try {
        const sessionKeysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const fullKey = sessionStorage.key(i);
            if (fullKey && fullKey.startsWith(TAXONOMY_CACHE_PREFIX)) {
                const key = fullKey.substring(TAXONOMY_CACHE_PREFIX.length);
                if (key.includes(pattern)) {
                    sessionKeysToRemove.push(fullKey);
                }
            }
        }
        sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
    }
    catch (error) {
        console.warn('Failed to clear sessionStorage cache with pattern:', error);
    }
    console.log(`Taxonomy cache cleared for pattern: ${keyPattern} (${keysToDelete.length} items)`);
};
/**
 * @deprecated This function loads too much data client-side. Use getTaxonomyEnrichedOffering for individual offerings instead.
 * Gets enriched offerings for multiple locales from the taxonomy API
 * @param supportedLocales Array of locale strings (e.g., ['en-US', 'de-DE'])
 * @returns Promise that resolves to an object with locale keys and offering arrays as values, or empty array on error
 */
const getTaxonomyEnrichedOfferings = async (supportedLocales) => {
    try {
        const enrichedOfferings = {};
        if (!PUBLIC_API_KEY || !apiUrl) {
            console.log("Missing 'PUBLIC_API_KEY' or 'apiUrl' for taxonomy middleware api call");
            return [];
        }
        const endpoint = apiUrl;
        for (const locale of supportedLocales) {
            console.log(`Getting enrichedOfferings for locale: ${locale}`);
            const response = await fetch(`${endpoint}/latest/offerings/${locale}?cache=true`, {
                method: 'GET',
                headers: { 'x-api-key': PUBLIC_API_KEY },
            });
            if (!response.ok) {
                console.error(`Failed to fetch enriched offerings data: ${response.status} ${response.statusText}`);
                return [];
            }
            const localeSpecificEnrichedOfferings = await response.json();
            console.log(`Taxonomy for ${locale} loaded ${localeSpecificEnrichedOfferings.length}`);
            enrichedOfferings[locale] = localeSpecificEnrichedOfferings;
        }
        return enrichedOfferings;
    }
    catch (error) {
        console.error(`Failed to retrieve enriched offerings from the Taxonomy API: ${error}`);
        return [];
    }
};
/**
 * Gets the current locale from window.disw.locales or falls back to 'en-US'
 * @returns The current locale string
 */
const getCurrentLocale = () => {
    try {
        // @ts-ignore - window.disw may not be typed
        return window?.disw?.locales?.getCurrentLocaleCode?.() || 'en-US';
    }
    catch {
        return 'en-US';
    }
};
/**
 * Gets a single enriched offering by URI and locale from the taxonomy API
 * @param params Object containing locale and offeringUri, or just offeringUri (legacy)
 * @param legacyOfferingUri Legacy parameter for backwards compatibility
 * @returns Promise that resolves to the enriched offering or null on error
 */
const getTaxonomyEnrichedOffering = async (params, legacyOfferingUri) => {
    // Handle both new object syntax and legacy parameters
    let locale;
    let offeringUri;
    if (typeof params === 'string') {
        // Legacy usage: getTaxonomyEnrichedOffering(locale, offeringUri)
        locale = params;
        offeringUri = legacyOfferingUri;
    }
    else {
        // New usage: getTaxonomyEnrichedOffering({ locale?, offeringUri })
        locale = params.locale || getCurrentLocale();
        offeringUri = params.offeringUri;
    }
    const cacheKey = generateCacheKey(locale, 'offering', offeringUri);
    // Check cache first
    const cachedValue = getFromCache(cacheKey);
    if (cachedValue !== undefined) {
        console.log(`Cache hit for enriched offering: ${locale}/${offeringUri}`);
        return cachedValue;
    }
    try {
        if (!PUBLIC_API_KEY || !apiUrl) {
            console.log("Missing 'PUBLIC_API_KEY' or 'apiUrl' for taxonomy middleware api call");
            return null;
        }
        console.log(`Getting enriched offering for locale: ${locale}, URI: ${offeringUri}`);
        const response = await fetch(`${apiUrl}/latest/offerings/${locale}/${offeringUri}?cache=true`, {
            method: 'GET',
            headers: { 'x-api-key': PUBLIC_API_KEY },
        });
        if (!response.ok) {
            console.error(`Failed to fetch enriched offering data: ${response.status} ${response.statusText}`);
            return null;
        }
        const enrichedOffering = await response.json();
        console.log(`Enriched offering loaded for ${locale}/${offeringUri}: ${enrichedOffering.name}`);
        // Cache the successful result
        setToCache(cacheKey, enrichedOffering);
        return enrichedOffering;
    }
    catch (error) {
        console.error(`Failed to retrieve enriched offering from the Taxonomy API: ${error}`);
        return null;
    }
};
/**
 * Gets a single enriched seller by URI and locale from the taxonomy API
 * @param params Object containing locale and sellerUri, or just sellerUri (legacy)
 * @param legacySellerUri Legacy parameter for backwards compatibility
 * @returns Promise that resolves to the enriched seller or null on error
 */
const getTaxonomyEnrichedSeller = async (params, legacySellerUri) => {
    // Handle both new object syntax and legacy parameters
    let locale;
    let sellerUri;
    if (typeof params === 'string') {
        // Legacy usage: getTaxonomyEnrichedSeller(locale, sellerUri)
        locale = params;
        sellerUri = legacySellerUri;
    }
    else {
        // New usage: getTaxonomyEnrichedSeller({ locale?, sellerUri })
        locale = params.locale || getCurrentLocale();
        sellerUri = params.sellerUri;
    }
    const cacheKey = generateCacheKey(locale, 'seller', sellerUri);
    // Check cache first
    const cachedValue = getFromCache(cacheKey);
    if (cachedValue !== undefined) {
        console.log(`Cache hit for enriched seller: ${locale}/${sellerUri}`);
        return cachedValue;
    }
    try {
        if (!PUBLIC_API_KEY || !apiUrl) {
            console.log("Missing 'PUBLIC_API_KEY' or 'apiUrl' for taxonomy middleware api call");
            return null;
        }
        console.log(`Getting enriched seller for locale: ${locale}, URI: ${sellerUri}`);
        const response = await fetch(`${apiUrl}/latest/sellers/${locale}/${sellerUri}?cache=true`, {
            method: 'GET',
            headers: { 'x-api-key': PUBLIC_API_KEY },
        });
        if (!response.ok) {
            console.error(`Failed to fetch enriched seller data: ${response.status} ${response.statusText}`);
            return null;
        }
        const enrichedSeller = await response.json();
        console.log(`Enriched seller loaded for ${locale}/${sellerUri}: ${enrichedSeller.name}`);
        // Cache the successful result
        setToCache(cacheKey, enrichedSeller);
        return enrichedSeller;
    }
    catch (error) {
        console.error(`Failed to retrieve enriched seller from the Taxonomy API: ${error}`);
        return null;
    }
};
/**
 * Gets the URL for an offering by URI and locale
 * @param params Object containing uri and optional locale
 * @returns Promise that resolves to the offering URL string or null on error
 */
const getTaxonomyOfferingUrl = async (params) => {
    const { uri, locale = getCurrentLocale() } = params;
    try {
        const offering = await getTaxonomyEnrichedOffering({ locale, offeringUri: uri });
        return offering?.offeringPageUrl || null;
    }
    catch (error) {
        console.error(`Failed to get taxonomy offering URL: ${error}`);
        return null;
    }
};
/**
 * Gets the URL for a seller by URI and locale
 * @param params Object containing uri and optional locale
 * @returns Promise that resolves to the seller URL string or null on error
 */
const getTaxonomySellerUrl = async (params) => {
    const { uri, locale = getCurrentLocale() } = params;
    try {
        const seller = await getTaxonomyEnrichedSeller({ locale, sellerUri: uri });
        return seller?.sellerPageUrl || null;
    }
    catch (error) {
        console.error(`Failed to get taxonomy seller URL: ${error}`);
        return null;
    }
};
/**
 * Gets the URL for an offering by ecbeId and locale
 * @param params Object containing ecbeId and optional locale
 * @returns Promise that resolves to the offering URL string or null on error
 */
const getTaxonomyOfferingUrlByEcbeId = async (params) => {
    const { ecbeId, locale = getCurrentLocale() } = params;
    const cacheKey = generateCacheKey(locale, 'offering-ecbe', ecbeId);
    // Check cache first
    const cachedValue = getFromCache(cacheKey);
    if (cachedValue !== undefined) {
        console.log(`Cache hit for offering by ecbeId: ${locale}/${ecbeId}`);
        return cachedValue;
    }
    try {
        if (!PUBLIC_API_KEY || !apiUrl) {
            console.log("Missing 'PUBLIC_API_KEY' or 'apiUrl' for taxonomy middleware api call");
            return null;
        }
        console.log(`Getting offering URL by ecbeId: ${ecbeId}`);
        const response = await fetch(`${apiUrl}/latest/offerings?ecbeId=${ecbeId}&cache=true`, {
            method: 'GET',
            headers: { 'x-api-key': PUBLIC_API_KEY },
        });
        if (!response.ok) {
            console.error(`Failed to fetch offering by ecbeId: ${response.status} ${response.statusText}`);
            return null;
        }
        const offerings = await response.json();
        if (!offerings || offerings.length === 0) {
            console.log(`No offerings found for ecbeId: ${ecbeId}`);
            return null;
        }
        // Get the first offering's URL
        const firstOffering = offerings[0];
        const offeringUrl = firstOffering.offeringPageUrl;
        console.log(`Found offering URL for ecbeId ${ecbeId}: ${offeringUrl} (from offering: ${firstOffering.name})`);
        // Cache the URL result
        setToCache(cacheKey, offeringUrl);
        // Also cache the individual offering for future URI-based lookups
        const offeringCacheKey = generateCacheKey(locale, 'offering', firstOffering.uri);
        setToCache(offeringCacheKey, firstOffering);
        return offeringUrl;
    }
    catch (error) {
        console.error(`Failed to get taxonomy offering URL by ecbeId: ${error}`);
        return null;
    }
};
export default {
    getTaxonomyEnrichedOfferings,
    getTaxonomyEnrichedOffering,
    getTaxonomyEnrichedSeller,
    getTaxonomyOfferingUrl,
    getTaxonomySellerUrl,
    getTaxonomyOfferingUrlByEcbeId,
    clearTaxonomyCache,
};
